import { useEffect, useState, type FormEvent } from 'react';
import { ChevronDown, KeyRound, Link2, LogIn, LogOut, ReceiptText, Settings2, ShieldCheck, Ticket, UserCog, Users, WalletCards } from 'lucide-react';
import { api, ApiError } from '../api';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import type { ViewKey } from '../components/AppShell';
import { formatMoney, formatTime, roleLabel, statusLabel } from '../utils';
import { Badge, Button, EmptyBlock, Field, PageHeader, Panel, TextInput } from '../components/ui';

export function ProfilePage({ onNeedAuth, onNavigate }: { onNeedAuth: () => void; onNavigate: (view: ViewKey) => void }) {
  const { user, settings, permissions, logout, refresh } = useAuth();
  const { toast, confirm } = useUi();
  const [busy, setBusy] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [binding, setBinding] = useState<any>(null);
  const [bindCode, setBindCode] = useState('');
  const loadBinding = async () => { try { const r = await api<any>('/api/v2/binding'); setBinding(r.binding); } catch {} };
  useEffect(() => { if (user) void loadBinding(); }, [user?.id]);
  const generateBind = async () => { try { const r = await api<{bindCode:string}>('/api/v2/binding/code', { method:'POST' }); setBindCode(r.bindCode); await loadBinding(); toast('绑定码已生成', 'success'); } catch(e) { toast(e instanceof ApiError ? e.message : '生成失败','error'); } };

  if (!user) {
    return (
      <>
        <PageHeader title="我的账户" description="登录后可查看账户资料和管理功能。" />
        <EmptyBlock title="尚未登录" description="点击下方按钮登录或注册。" action={<Button variant="primary" onClick={onNeedAuth}><LogIn size={17} /> 登录 / 注册</Button>} />
      </>
    );
  }

  const changePassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    setBusy(true);
    try {
      await api('/api/me/password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword: String(form.get('currentPassword') || ''), newPassword: String(form.get('newPassword') || '') })
      });
      formEl.reset();
      setPasswordOpen(false);
      await refresh();
      toast('密码已修改，其他设备会话已注销', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '修改失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  const doLogout = async () => {
    const approved = await confirm({ title: '退出登录', body: '确认退出当前账号？', danger: true, confirmText: '退出登录' });
    if (approved) await logout();
  };

  const adminEntries: Array<{ view: ViewKey; title: string; description: string; icon: typeof Users }> = [
    { view: 'members', title: '成员管理', description: '余额、密码、冻结与角色', icon: Users },
    { view: 'redeem', title: '兑换码', description: '创建、停用和使用记录', icon: Ticket },
    { view: 'settings', title: '系统设置', description: '注册、邀请码和业务规则', icon: Settings2 },
    { view: 'audit', title: '审计日志', description: '管理员操作与资金变动', icon: ReceiptText }
  ];

  return (
    <>
      <PageHeader title="我的账户" description="查看账户资料和可用管理功能。" />
      <Panel title="游戏角色绑定" eyebrow="BINDING"><div className="profile-card"><div className="profile-avatar"><Link2 size={22}/></div><div><h3>{binding?.status === 'active' ? binding.gameName : '尚未绑定'}</h3><p>{binding?.status === 'active' ? 'XUID ' + binding.xuid : '绑定后才能充值、提现、转账、上架和购买卡密'}</p></div></div><div className="actions" style={{marginTop:14}}><Button variant="primary" onClick={() => void generateBind()}><Link2 size={16}/>{binding?.status === 'active' ? '重新生成绑定码' : '生成绑定码'}</Button></div>{bindCode && <div className="created-code"><span>在游戏聊天栏发送绑定码：</span><code>{bindCode}</code></div>}</Panel>

      <Panel title="账户资料" eyebrow="ACCOUNT" className="top-gap">
        <div className="profile-card">
          <div className="profile-avatar">{user.username.slice(0, 1).toUpperCase()}</div>
          <div><h3>{user.displayName}</h3><p>@{user.username}</p></div>
        </div>
        <dl className="detail-list">
          <div><dt>账号角色</dt><dd><Badge tone={user.role === 'superadmin' ? 'gold' : user.role === 'admin' ? 'active' : 'muted'}>{roleLabel[user.role]}</Badge></dd></div>
          <div><dt>账号状态</dt><dd><Badge tone={user.status === 'active' ? 'good' : 'danger'}>{statusLabel[user.status]}</Badge></dd></div>
          <div><dt>可用余额</dt><dd className="balance-line"><WalletCards size={17} /> {formatMoney(user.balanceCents, settings.currency)}</dd></div>
          <div><dt>注册时间</dt><dd>{formatTime(user.createdAt)}</dd></div>
          <div><dt>上次登录</dt><dd>{formatTime(user.lastLoginAt)}</dd></div>
        </dl>
      </Panel>

      {permissions.isStaff && (
        <Panel title="管理后台" eyebrow="ADMIN" className="top-gap">
          <div className="admin-entry-grid">
            {adminEntries.map(item => {
              const Icon = item.icon;
              return <button key={item.view} className="admin-entry" onClick={() => onNavigate(item.view)}><Icon size={20} /><span><strong>{item.title}</strong><small>{item.description}</small></span></button>;
            })}
          </div>
        </Panel>
      )}

      <Panel title="安全设置" eyebrow="SECURITY" className="top-gap" action={<Button variant="default" onClick={() => setPasswordOpen(value => !value)}><KeyRound size={16} />修改密码<ChevronDown size={15} /></Button>}>
        {!passwordOpen ? <p className="muted">修改密码表单默认收起，需要时再展开。</p> : (
          <form className="stack-form" onSubmit={changePassword}>
            <p className="form-note"><ShieldCheck size={16} /> 新密码至少 4 位，修改后其他登录会话会立即失效。</p>
            <Field label="当前密码"><TextInput name="currentPassword" type="password" minLength={4} required /></Field>
            <Field label="新密码"><TextInput name="newPassword" type="password" minLength={4} maxLength={128} required /></Field>
            <Button variant="primary" type="submit" loading={busy}><KeyRound size={17} /> 更新密码</Button>
          </form>
        )}
      </Panel>

      <div className="profile-logout"><Button variant="danger" onClick={() => void doLogout()}><LogOut size={17} /> 退出登录</Button></div>
    </>
  );
}
